import React, { useState, forwardRef } from 'react';
import {
  Autocomplete,
  Grid,
  Button,
  InputAdornment,
  InputBase,
  Menu,
  MenuItem as MuiMenuItem,
  menuItemClasses,
  styled,
  TextField,
  TextFieldProps,
  Tooltip,
  Chip,
} from '@mui/material';
import { Field, FieldProps, getIn } from 'formik';
import { ArrowDropDown } from '@mui/icons-material';
import { JItem } from '../../forms/jformik/jformik.types';
import { useJFormikContext } from '../jformik/jformik.context';
import { useBreakpointQuery } from '../../../utils';

export const FEmailToInput = forwardRef((jItem: JItem, ref) => {
  const { disabled = false, disableSubmitOnEnter = true } = useJFormikContext();
  const [inputValue, setInputValue] = useState<string>('');
  const [inputValueCC, setInputValueCC] = useState<string>('');
  const [inputValueBCC, setInputValueBCC] = useState<string>('');
  const [showCC, setShowCC] = useState<boolean>(false);
  const [showBCC, setShowBCC] = useState<boolean>(false);
  const smDown = useBreakpointQuery('sm-down');
  const [openTooltip, setOpenTooltip] = useState<boolean | undefined>(
    jItem?.tooltip?.open
  );
  const {
    multiple = false,
    freeSolo = false,
    freeSoloFormat,
    limitTags,
    name,
    label,
    options,
    tooltip,
  } = jItem;
  return (
    <Field name={jItem.name} innerRef>
      {({
        field: { name, value },
        form: { touched, errors, setFieldValue, values, submitCount },
      }: FieldProps) => {
        return (
          <Grid
            container
            alignContent={'space-between'}
            sx={{
              paddingY: 0.9,
              borderBottom: theme =>
                `1px solid ${theme.palette.action.disabledBackground}`,
            }}
          >
            <Grid container xs={10}>
              <Grid item xs={12}>
                <Autocomplete
                  disabled={disabled || jItem.disabled}
                  multiple={multiple}
                  freeSolo={freeSolo}
                  blurOnSelect={freeSolo ? 'mouse' : false}
                  limitTags={limitTags}
                  autoSelect
                  onChange={(event: any, newValue: any) => {
                    if (freeSolo && typeof newValue === 'string') {
                      setFieldValue(
                        name,
                        freeSoloFormat
                          ? freeSoloFormat(newValue)
                          : { id: 0, label: newValue, name: newValue }
                      );
                    } else if (freeSolo && multiple) {
                      const newArray = newValue.map((value: any) =>
                        typeof value === 'string'
                          ? freeSoloFormat
                            ? freeSoloFormat(value)
                            : { id: 0, label: value, name: value }
                          : value
                      );
                      setFieldValue(name, newArray);
                    } else {
                      setFieldValue(name, newValue);
                    }
                  }}
                  value={value}
                  options={
                    options
                      ? typeof options === 'function'
                        ? options(values)
                        : options
                      : []
                  }
                  getOptionLabel={(option: any) => {
                    if (typeof option === 'string') {
                      return option;
                    } else {
                      return (
                        option?.label ||
                        option?.title ||
                        option?.name ||
                        option?.email ||
                        ''
                      );
                    }
                  }}
                  renderInput={(params: TextFieldProps) => {
                    if (tooltip) {
                      return (
                        <Tooltip {...{ ...tooltip, open: openTooltip }}>
                          <TextField
                            {...params}
                            onKeyPress={e => {
                              disableSubmitOnEnter &&
                                e.key === 'Enter' &&
                                e.preventDefault();
                            }}
                            onFocus={() => {
                              if (openTooltip) {
                                setOpenTooltip(false);
                              }
                            }}
                            label={label}
                            variant="outlined"
                            // size={smDown ? 'small' : 'medium'}
                            error={
                              !!(
                                getIn(errors, name) &&
                                (getIn(touched, name) || submitCount)
                              )
                            }
                            helperText={
                              jItem.jItemProps?.helperText
                                ? jItem.jItemProps?.helperText
                                : getIn(touched, name) || submitCount
                                ? getIn(errors, name)
                                : null
                            }
                          />
                        </Tooltip>
                      );
                    } else {
                      return (
                        <TextField
                          {...params}
                          onKeyPress={e => {
                            disableSubmitOnEnter &&
                              e.key === 'Enter' &&
                              e.preventDefault();
                          }}
                          sx={{
                            paddingLeft: 0,
                            paddingRight: 1,
                            boxSizing: 'border-box',
                            '.MuiOutlinedInput-root': {
                              padding: 0,
                            },
                            '.MuiOutlinedInput-notchedOutline': {
                              borderWidth: `0px !important`,
                            },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            sx: {
                              color: theme => theme.palette.primary.main,
                              fontWeight: 400,
                              fontSize: 14,
                            },
                            startAdornment: (
                              <>
                                <InputAdornment
                                  position="start"
                                  sx={{
                                    color: theme => theme.palette.text.disabled,
                                  }}
                                >
                                  To:{' '}
                                </InputAdornment>
                                {params?.InputProps?.startAdornment}
                              </>
                            ),
                          }}
                        />
                      );
                    }
                  }}
                  autoHighlight
                  {...jItem.jItemProps}
                  {...(jItem?.jItemProps?.renderTags
                    ? {
                        renderTags: (
                          val: readonly string[],
                          getTagProps: any
                        ) => jItem.jItemProps.renderTags(value, getTagProps),
                      }
                    : {})}
                />
              </Grid>
              {showCC && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    freeSolo
                    fullWidth
                    options={[]}
                    defaultValue={[]}
                    renderInput={params => (
                      <TextField
                        {...params}
                        onKeyPress={e => {
                          disableSubmitOnEnter &&
                            e.key === 'Enter' &&
                            e.preventDefault();
                        }}
                        sx={{
                          paddingLeft: 0,
                          paddingRight: 1,
                          boxSizing: 'border-box',
                          '.MuiOutlinedInput-root': {
                            padding: 0,
                          },
                          '.MuiOutlinedInput-notchedOutline': {
                            borderWidth: `0px !important`,
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            color: theme => theme.palette.primary.main,
                            fontWeight: 400,
                            fontSize: 14,
                          },
                          startAdornment: (
                            <>
                              <InputAdornment
                                position="start"
                                sx={{
                                  color: theme => theme.palette.text.disabled,
                                }}
                              >
                                Cc:{' '}
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    value={value}
                    onChange={(event: any, newValue: any) => {
                      setFieldValue(name, newValue);
                    }}
                    inputValue={inputValueCC}
                    onInputChange={(event, inputValue) => {
                      const options = inputValue.split(/[, ]+/);

                      if (options.length > 1) {
                        setFieldValue(
                          name,
                          value
                            .concat(options)
                            .map((x: any) => x?.trim())
                            .filter((x: any) => x)
                        );

                        setInputValueCC('');
                      } else {
                        setInputValueCC(inputValue);
                      }
                    }}
                    onBlur={(event: any) => {
                      const option = (event?.target?.value || '').trim();

                      if (option) {
                        setFieldValue(name, [...(value || []), option]);
                        setInputValueCC('');
                      }
                    }}
                    {...jItem.jItemProps}
                    {...(jItem?.jItemProps?.renderTags
                      ? {
                          renderTags: (
                            val: readonly string[],
                            getTagProps: any
                          ) => jItem.jItemProps.renderTags(value, getTagProps),
                        }
                      : {})}
                  />
                </Grid>
              )}
              {showBCC && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    freeSolo
                    fullWidth
                    options={[]}
                    defaultValue={[]}
                    renderInput={params => (
                      <TextField
                        {...params}
                        onKeyPress={e => {
                          disableSubmitOnEnter &&
                            e.key === 'Enter' &&
                            e.preventDefault();
                        }}
                        sx={{
                          paddingLeft: 0,
                          paddingRight: 1,
                          boxSizing: 'border-box',
                          '.MuiOutlinedInput-root': {
                            padding: 0,
                          },
                          '.MuiOutlinedInput-notchedOutline': {
                            borderWidth: `0px !important`,
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            color: theme => theme.palette.primary.main,
                            fontWeight: 400,
                            fontSize: 14,
                          },
                          startAdornment: (
                            <>
                              <InputAdornment
                                position="start"
                                sx={{
                                  color: theme => theme.palette.text.disabled,
                                }}
                              >
                                Bcc:{' '}
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    value={value}
                    onChange={(event: any, newValue: any) => {
                      setFieldValue(name, newValue);
                    }}
                    inputValue={inputValueBCC}
                    onInputChange={(event, inputValue) => {
                      const options = inputValue.split(/[, ]+/);

                      if (options.length > 1) {
                        setFieldValue(
                          name,
                          value
                            .concat(options)
                            .map((x: any) => x?.trim())
                            .filter((x: any) => x)
                        );

                        setInputValueBCC('');
                      } else {
                        setInputValueBCC(inputValue);
                      }
                    }}
                    onBlur={(event: any) => {
                      const option = (event?.target?.value || '').trim();

                      if (option) {
                        setFieldValue(name, [...(value || []), option]);
                        setInputValueBCC('');
                      }
                    }}
                    {...jItem.jItemProps}
                    {...(jItem?.jItemProps?.renderTags
                      ? {
                          renderTags: (
                            val: readonly string[],
                            getTagProps: any
                          ) => jItem.jItemProps.renderTags(value, getTagProps),
                        }
                      : {})}
                  />
                </Grid>
              )}
            </Grid>
            <Grid
              container
              direction={'row'}
              alignContent={'end'}
              alignSelf={'end'}
              xs={2}
            >
              {!showCC && (
                <Button
                  disabled={disabled || jItem.disabled}
                  sx={{
                    fontWeight: 400,
                    textTransform: 'unset',
                    minWidth: 'unset',
                  }}
                  onClick={() => setShowCC(showCC => !showCC)}
                >
                  Cc
                </Button>
              )}
              {!showBCC && (
                <Button
                  disabled={disabled || jItem.disabled}
                  sx={{
                    fontWeight: 400,
                    textTransform: 'unset',
                    minWidth: 'unset',
                    // marginLeft: 2,
                  }}
                  onClick={() => setShowBCC(showBCC => !showBCC)}
                >
                  Bcc
                </Button>
              )}
            </Grid>
          </Grid>
        );
      }}
    </Field>
  );
});

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  [`&.${menuItemClasses.root}`]: {
    fontSize: 14,
    color: theme.palette.primary.main,
  },
}));
